import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { authService, dataService } from '../services/api';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) throw new Error('useData must be used within a DataProvider');
  return context;
};

const initialModuleState = {
  checklist: { items: {} },
  wordCount: { currentWords: 0, sessions: [] },
  brainstorm: {},
  framework: {},
  publishing: { kdp: {}, d2d: {} },
  marketing: { tasks: {} },
  expenses: { items: [] },
  revenue: { items: [] },
  foundation: { bookFormats: {}, targets: {} },
  strategy: { focusChannels: {}, actions: [] },
  preLaunch: { plan: {}, tasks: [] },
  launch: { plan: {}, tasks: [] },
  postLaunch: { strategy: {}, tasks: [] },
  contentPlanner: { content: [] },
  monthOutlook: { calendars: [] },
  readerEngagement: { interactions: [] },
  faithJournal: { entries: [] }
};

export const DataProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentBook, setCurrentBook] = useState(null);
  const [books, setBooks] = useState([]);
  const [data, setData] = useState(initialModuleState);
  const [loading, setLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [authError, setAuthError] = useState(null);

  const saveTimeoutRef = useRef(null);
  const pendingChangesRef = useRef(new Set());

  const updateUserSession = (updates) => {
    setCurrentUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      sessionStorage.setItem('gospelwise_user', JSON.stringify(updated));
      return updated;
    });
  };

  useEffect(() => {
    const authenticate = async () => {
      // 1. Extract Token (Hash-aware)
      const searchParams = new URLSearchParams(window.location.search);
      let token = searchParams.get('token');
      if (!token && window.location.hash.includes('?')) {
        const hashQuery = window.location.hash.split('?')[1];
        token = new URLSearchParams(hashQuery).get('token');
      }
      
      const storedUser = sessionStorage.getItem('gospelwise_user');
      
      try {
        let userToSession = null;

        if (token) {
          console.log("Token detected. Verifying with Hub...");
          const hubResponse = await authService.verifyToken(token);
          
          // Debug: Log exactly what the Hub sent
          console.log("Hub Raw Response:", { valid: hubResponse.valid, error: hubResponse.error });

          // Handle if Hub returns { error: '...' }
          if (hubResponse.error || !hubResponse.valid) {
            throw new Error(hubResponse.error || 'Hub did not verify this token');
          }

          // 2. RESILIENT DATA MAPPING
          // Look for user data in multiple common structures
          const hubUserData = hubResponse.user || hubResponse.data || hubResponse;
          const hubId = hubUserData.hub_id || hubUserData.id || hubUserData.user_id;
          
          if (hubId || hubUserData.email) {
            console.log("Identity confirmed. Syncing with Spoke DB...");
            
            // Sync with Spoke Database
            const syncResult = await authService.syncUser({
              hub_id: hubId || 0,
              email: hubUserData.email,
              name: hubUserData.name || hubUserData.display_name || 'Author',
              token
            });
            if (!syncResult.success || !syncResult.id || String(syncResult.hub_id) !== String(hubId)) {
              throw new Error('Spoke could not confirm the verified Hub identity.');
            }
            
            const effectiveAccessType = syncResult.access_type || 'trial';

            userToSession = {
              ...hubUserData,
              hub_id: syncResult.hub_id,
              local_id: syncResult.id,
              access_type: effectiveAccessType,
              single_amt: typeof syncResult.single_amt === 'number' ? syncResult.single_amt : (hubUserData.single_amt || 0),
              email: syncResult.email,
              name: syncResult.name || hubUserData.name || hubUserData.display_name || 'Author'
            };
            
            // Save to session to persist across reloads
            sessionStorage.setItem('gospelwise_user', JSON.stringify(userToSession));
            sessionStorage.setItem('gospelwise_handoff_token', token);
            
            // Clean URL
            const cleanUrl = window.location.origin + window.location.pathname + window.location.hash.split('?')[0];
            window.history.replaceState({}, document.title, cleanUrl);
          } else {
            console.error("Critical: Could not find user ID in Hub response", { valid: hubResponse.valid });
            throw new Error("Invalid user identity structure received from Hub.");
          }
        } else if (storedUser) {
          const storedToken = sessionStorage.getItem('gospelwise_handoff_token');
          if (!storedToken) throw new Error('Please launch FormatterPro from the Hub to verify your session.');
          const hubResponse = await authService.verifyToken(storedToken);
          if (hubResponse.error || !hubResponse.valid) {
            throw new Error(hubResponse.error || 'Hub did not verify this token');
          }
          const syncResult = await authService.syncUser({ token: storedToken });
          if (!syncResult.success || !syncResult.id || !syncResult.hub_id) {
            throw new Error('Spoke could not confirm the verified Hub identity.');
          }
          userToSession = JSON.parse(storedUser);
          if (String(userToSession.hub_id) !== String(syncResult.hub_id)) {
            throw new Error('Stored session does not match the verified Hub identity.');
          }
          userToSession = {
            ...userToSession,
            hub_id: syncResult.hub_id,
            local_id: syncResult.id,
            access_type: syncResult.access_type || 'trial',
            single_amt: typeof syncResult.single_amt === 'number' ? syncResult.single_amt : 0,
            email: syncResult.email,
            name: syncResult.name
          };
          sessionStorage.setItem('gospelwise_user', JSON.stringify(userToSession));
        }

        if (userToSession) {
          setCurrentUser(userToSession);
          await refreshBooks(userToSession.local_id);
        } else {
          console.warn("No session or token. Returning to Hub.");
          const isDevOrPreview = window.location.hostname.includes('webcontainer') ||
                                window.location.hostname.includes('localhost') ||
                                window.location.hostname.includes('127.0.0.1');
          if (!isDevOrPreview) {
            window.location.href = 'https://www.gospelwiseauthor.app/#/login';
          } else {
            setAuthError("No authorization token detected. Please launch FormatterPro from the GospelWise Author Hub.");
          }
        }
      } catch (err) {
        console.error("Authentication Error:", err.message);
        sessionStorage.removeItem('gospelwise_user');
        sessionStorage.removeItem('gospelwise_handoff_token');
        setAuthError(err.message || "Unauthorized Access");
        // Only redirect after a delay so the user can see the error
        const isDevOrPreview = window.location.hostname.includes('webcontainer') ||
                              window.location.hostname.includes('localhost') ||
                              window.location.hostname.includes('127.0.0.1');
        if (!isDevOrPreview) {
          setTimeout(() => {
            if (!currentUser) window.location.href = 'https://www.gospelwiseauthor.app/#/login';
          }, 4000);
        }
      } finally {
        setLoading(false);
      }
    };

    authenticate();
  }, []);

  const refreshBooks = async (userId) => {
    if (!userId) return;
    try {
      const fetchedBooks = await dataService.getBooks(userId);
      setBooks(Array.isArray(fetchedBooks) ? fetchedBooks : []);
      
      const lastBookId = sessionStorage.getItem('last_active_book_id');
      if (lastBookId && !currentBook) {
        const found = fetchedBooks.find(b => String(b.id) === String(lastBookId));
        if (found) await selectBook(found);
      }
    } catch (err) {
      console.error("Book fetch failed:", err);
    }
  };

  const selectBook = async (book) => {
    if (!book || !book.id) return;
    setLoading(true);
    setCurrentBook(book);
    sessionStorage.setItem('last_active_book_id', book.id);
    try {
      const moduleData = await dataService.getModuleData(book.id);
      const mergedData = JSON.parse(JSON.stringify(initialModuleState));
      if (Array.isArray(moduleData)) {
        moduleData.forEach(row => {
          if (row.content_json && mergedData.hasOwnProperty(row.section_name)) {
            try {
              mergedData[row.section_name] = JSON.parse(row.content_json);
            } catch (e) {
              console.error("JSON Parse Error for section:", row.section_name);
            }
          }
        });
      }
      setData(mergedData);
    } catch (err) {
      console.error("Project data load failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const createBook = async (bookData) => {
    if (!currentUser) return;
    setIsSyncing(true);
    try {
      const result = await dataService.saveBook(currentUser.local_id, bookData);
      if (result.success) {
        await refreshBooks(currentUser.local_id);
        const fetched = await dataService.getBooks(currentUser.local_id);
        const newBook = fetched.find(b => String(b.id) === String(result.id));
        if (newBook) await selectBook(newBook);
        return newBook;
      }
    } catch (err) {
      console.error("Book creation failed:", err);
    } finally {
      setIsSyncing(false);
    }
  };

  const triggerAutosave = (section, latestData) => {
    if (!currentBook?.id) return;
    pendingChangesRef.current.add(section);
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      setIsSyncing(true);
      const sections = Array.from(pendingChangesRef.current);
      pendingChangesRef.current.clear();
      try {
        await Promise.all(sections.map(sec => 
          dataService.saveModuleData(currentBook.id, sec, latestData[sec])
        ));
      } catch (err) {
        console.error("Autosave Error:", err);
      } finally {
        setTimeout(() => setIsSyncing(false), 800);
      }
    }, 2000);
  };

  const updateData = (section, newData) => {
    if (!currentBook) return;
    setData(prev => {
      const newState = { ...prev, [section]: { ...prev[section], ...newData } };
      triggerAutosave(section, newState);
      return newState;
    });
  };

  const deleteBook = async (bookId) => {
    try {
      await dataService.deleteBook(bookId);
      if (String(currentBook?.id) === String(bookId)) exitProject();
      await refreshBooks(currentUser.local_id);
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const exitProject = () => {
    setCurrentBook(null);
    setData(initialModuleState);
    sessionStorage.removeItem('last_active_book_id');
  };

  const calculateReadiness = () => {
    const productionModules = [
      { key: 'checklist', items: 8 },
      { key: 'publishing_kdp', items: 10 },
      { key: 'publishing_d2d', items: 7 },
      { key: 'marketing', items: 8 }
    ];
    let totalTasks = productionModules.reduce((acc, mod) => acc + mod.items, 0);
    let finishedTasks = 0;
    finishedTasks += Object.values(data.checklist?.items || {}).filter(Boolean).length;
    finishedTasks += Object.values(data.publishing?.kdp || {}).filter(Boolean).length;
    finishedTasks += Object.values(data.publishing?.d2d || {}).filter(Boolean).length;
    finishedTasks += Object.values(data.marketing?.tasks || {}).filter(Boolean).length;
    return totalTasks === 0 ? 0 : Math.round((finishedTasks / totalTasks) * 100);
  };

  return (
    <DataContext.Provider value={{ 
      data, updateData, currentUser, currentBook, books, 
      selectBook, createBook, deleteBook, calculateReadiness, 
      refreshBooks, loading, isSyncing, exitProject, authError,
      updateUserSession 
    }}>
      {children}
    </DataContext.Provider>
  );
};